import { useMemo, useState } from 'react';
import { Alert, FormControlLabel, FormHelperText, MenuItem, Switch, TextField } from '@mui/material';
import { AdminFormDialog } from '@shared/components';
import { api } from '@core/api.js';
import { useNotify } from '@shared/hooks/useNotify.js';
import { useFeature } from '@core/offerContext.jsx';
import { apiErrorMessage } from '@shared/utils/apiErrorMessage.js';

const STATUS_OPTIONS = [
  { value: 'lead', label: 'Lead' },
  { value: 'qualified', label: 'Qualifié' },
  { value: 'customer', label: 'Client' },
  { value: 'lost', label: 'Perdu' },
];

function isValidEmail(value) {
  if (!value) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * @param {{ contact?: object, onClose: (didMutate?: boolean) => void, preset?: 'client' | null }} props
 */
export default function ContactFormDialog({ contact, onClose, preset = null }) {
  const isNew = !contact?.id;
  const notify = useNotify();
  const customFieldsEnabled = useFeature('contact.customFields');
  const asClient = preset === 'client' || contact?.__preset === 'client';

  const [form, setForm] = useState(() => ({
    name: contact?.name || '',
    email: contact?.email || '',
    phone: contact?.phone || '',
    company: contact?.company || '',
    source: contact?.source || '',
    status: contact?.status || (asClient ? 'customer' : 'lead'),
    notes: contact?.notes || '',
    tags: (contact?.tags || []).join(', '),
    homeMeetingCompleted: contact?.id
      ? !!contact?.homeMeetingCompleted
      : (asClient ? true : !!contact?.homeMeetingCompleted),
  }));
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const touch = (k) => setTouched((t) => ({ ...t, [k]: true }));

  const hasIdentity = !!(form.email.trim() || form.phone.trim());
  const bookingWarn = form.homeMeetingCompleted && !hasIdentity;

  const errors = useMemo(() => {
    const e = {};
    if (!form.name.trim()) e.name = 'Le nom est requis.';
    if (form.email && !isValidEmail(form.email)) e.email = 'Adresse e-mail invalide.';
    if (form.homeMeetingCompleted && !hasIdentity) {
      e.identity = 'E-mail ou téléphone requis pour la réservation en ligne.';
    }
    return e;
  }, [form, hasIdentity]);

  const showError = (field) => !!touched[field] && !!errors[field];
  const canSubmit = Object.keys(errors).length === 0;

  const onStatusChange = (value) => {
    setForm((s) => {
      const next = { ...s, status: value };
      if (value === 'customer') {
        next.homeMeetingCompleted = true;
      }
      return next;
    });
  };

  const onBookingToggle = (checked) => {
    setForm((s) => {
      const next = { ...s, homeMeetingCompleted: checked };
      if (checked && s.status !== 'lost') {
        next.status = 'customer';
      }
      return next;
    });
  };

  const submit = async () => {
    setTouched({ name: true, email: true, identity: true });
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      };
      if (customFieldsEnabled) payload.customFields = contact?.customFields || {};
      if (isNew) await api.post('/v1/contacts', payload);
      else await api.patch(`/v1/contacts/${contact.id}`, payload);
      notify.success(
        isNew
          ? (asClient ? 'Client créé — réservation en ligne activée' : 'Contact créé')
          : 'Contact mis à jour',
      );
      onClose(true);
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Enregistrement du contact impossible.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminFormDialog
      title={isNew ? (asClient ? 'Nouveau client' : 'Nouveau contact') : 'Modifier le contact'}
      onClose={() => onClose(false)}
      onSubmit={submit}
      saving={saving}
      submitDisabled={!canSubmit && Object.keys(touched).length > 0}
      submitLabel={isNew ? 'Créer' : 'Enregistrer'}
    >
      {asClient && isNew && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Ce client pourra réserver sur le site via « Déjà client(e) » (e-mail ou téléphone + réservation en ligne).
        </Alert>
      )}
      <TextField
        label="Nom"
        value={form.name}
        onChange={(e) => setField('name', e.target.value)}
        onBlur={() => touch('name')}
        required
        fullWidth
        error={showError('name')}
        helperText={showError('name') ? errors.name : ' '}
      />
      <TextField
        label="Email"
        type="email"
        value={form.email}
        onChange={(e) => setField('email', e.target.value)}
        onBlur={() => touch('email')}
        fullWidth
        error={showError('email')}
        helperText={showError('email') ? errors.email : ' '}
      />
      <TextField
        label="Téléphone"
        value={form.phone}
        onChange={(e) => setField('phone', e.target.value)}
        fullWidth
      />
      <TextField
        label="Société"
        value={form.company}
        onChange={(e) => setField('company', e.target.value)}
        fullWidth
      />
      <TextField
        label="Source"
        value={form.source}
        onChange={(e) => setField('source', e.target.value)}
        fullWidth
        helperText="Ex. site web, bouche-à-oreille"
      />
      <TextField
        label="Statut"
        select
        value={form.status}
        onChange={(e) => onStatusChange(e.target.value)}
        fullWidth
      >
        {STATUS_OPTIONS.map((s) => (
          <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
        ))}
      </TextField>
      <TextField
        label="Tags"
        value={form.tags}
        onChange={(e) => setField('tags', e.target.value)}
        fullWidth
        helperText="Séparés par des virgules"
      />
      <TextField
        label="Notes"
        multiline
        rows={3}
        value={form.notes}
        onChange={(e) => setField('notes', e.target.value)}
        fullWidth
      />
      <FormControlLabel
        control={(
          <Switch
            checked={form.homeMeetingCompleted}
            onChange={(e) => onBookingToggle(e.target.checked)}
          />
        )}
        label="Autoriser la réservation en ligne"
      />
      <FormHelperText sx={{ mt: -1, mb: 1 }}>
        Rencontre à domicile effectuée — requis pour « Déjà client(e) » sur /demande-de-reservation.
      </FormHelperText>
      {bookingWarn && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Ajoutez un e-mail ou un téléphone pour que la personne puisse se faire reconnaître sur le site.
        </Alert>
      )}
    </AdminFormDialog>
  );
}
